import { useState } from 'react';
import MercariService from './mercariService';
import { useSelector } from 'react-redux';

function useMercari() {
  const [pendingChanges, setPendingChanges] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [negotiationLists, setNegotiationLists] = useState([]);
  const [isLoadingNegotiations, setIsLoadingNegotiations] = useState(false);
  const [negotiationError, setNegotiationError] = useState(null);
  const [negotiationPagination, setNegotiationPagination] = useState(null);
  const shopName = useSelector((state) => state.shop.name);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [mercariAccounts, setMercariAccounts] = useState([]);

  const takeCase = async (changeId) => {
    setIsLoading(true);
    try {
      const response = await MercariService.takeCase(changeId);
      if (response.data?.success) {
        return true;
      } else {
        setError(response.data?.message || 'Failed to take case');
        return false;
      }
    } catch (err) {
      console.error('Error in takeCase:', err);
      // The API answers 400 when nothing was claimed (updatedCount 0) — almost
      // always because another admin got there first.
      setError(err.response?.status === 400
        ? 'เคสนี้มีแอดมินคนอื่นรับไปแล้ว หรือไม่พบเคส กรุณารีเฟรชหน้า'
        : (err.response?.data?.message || 'An error occurred while taking the case'));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const completeCase = async (payload) => {
    setIsLoading(true);
    try {
      const updatedPayload = { ...payload, shop: shopName };
      const response = await MercariService.completeCase(updatedPayload);
      if (response.data.success !== true) {
        setError(response.data.message);
        return false;
      }
      return true;
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.message || 'An error occurred.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Add Mercari Auction Item
  const addAuctionItem = async (payload) => {
    setIsLoading(true);
    try {
      const updatedPayload = { ...payload, shop: shopName };
      const response = await MercariService.addAuctionItem(updatedPayload);
      if (response.data?.success) {
        // Fail-open path: added, but the live Mercari check was unavailable
        return { success: true, warning: response.data.warning || null };
      } else if (response.data?.needsConfirm) {
        // Mercari says not-an-auction right now — caller confirms and resends with forceAdd
        return { needsConfirm: true, message: response.data.message };
      } else {
        setError(response.data?.message || 'Failed to add auction item');
        return false;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Edit a registered auction item (fix wrong auction id / customer)
  const updateAuctionItem = async (payload) => {
    setIsLoading(true);
    try {
      const updatedPayload = { ...payload, shop: shopName };
      const response = await MercariService.updateAuctionItem(updatedPayload);
      if (response.data?.success) {
        return { success: true, warning: response.data.warning || null };
      }
      setError(response.data?.message || 'Failed to update auction item');
      return false;
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Get Auction Items by Finish Date
  const getAuctionItemsByFinishDate = async (payload) => {
    setIsLoading(true);
    try {
      const updatedPayload = { ...payload, shop: shopName };
      const response = await MercariService.getAuctionItemsByFinishDate(updatedPayload);
      return response.data?.items || [];
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred.');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const setAuctionItemStatus = async ({ id, status }) => {
    setIsStatusLoading(true);
    try {
      const response = await MercariService.setAuctionItemStatus({ id, status });
      if (response.data.success !== true) {
        setError(response.data.message);
        return false;
      }
      return true;
    } catch (err) {
      setIsStatusLoading(false);
      setError(err.response?.data?.message || 'An error occurred.');
      return false;
    } finally {
      setIsStatusLoading(false);
    }
  };

  const getPendingChanges = async () => {
    setIsLoading(true);
    try {
      const response = await MercariService.getPendingChanges({shop: shopName});
      if (response.data) {
        setPendingChanges(response.data.changes);
        return response.data;
      } else {
        setError(response.data?.error || 'Failed to fetch pending changes');
        return [];
      }
    } catch (err) {
      console.error('Error in getPendingChanges:', err);
      setError(err.response?.data?.message || 'An error occurred while fetching pending changes');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getNegotiationLists = async (page = 1, showHiddenItems = false) => {
    setIsLoadingNegotiations(true);
    setNegotiationError(null);
    try {
      const response = await MercariService.getNegotiationLists(shopName, page, 50, showHiddenItems);
      if (response.data) {
        setNegotiationLists(response.data.items || []);
        setNegotiationPagination(response.data.pagination || null);
      }
    } catch (error) {
      setNegotiationError(error.response?.data?.message || error.message);
    } finally {
      setIsLoadingNegotiations(false);
    }
  };

  const addNegotiation = async (formData) => {
    setIsLoadingNegotiations(true);
    setNegotiationError(null);
    try {
      const updatedFormData = { ...formData, shop: shopName };
      const response = await MercariService.addNegotiation(updatedFormData);
      // Return the full response so component can show detailed messages
      return response.data;
    } catch (error) {
      setNegotiationError(error.response?.data?.message || 'เกิดข้อผิดพลาด');
      return null;
    } finally {
      setIsLoadingNegotiations(false);
    }
  };

  const untrackNegotiation = async (favoriteId, url = null) => {
    setIsLoadingNegotiations(true);
    setNegotiationError(null);
    try {
      const response = await MercariService.untrackNegotiation(favoriteId, shopName, url);
      if (response.data?.success) {
        return response.data; // Return full response to get action type
      } else {
        setNegotiationError(response.data?.message || 'ไม่สามารถดำเนินการได้');
        return { success: false, message: response.data?.message || 'ไม่สามารถดำเนินการได้' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'เกิดข้อผิดพลาด';
      setNegotiationError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setIsLoadingNegotiations(false);
    }
  };

  const updateNegotiationCustomer = async (favoriteId, customerId) => {
    try {
      const response = await MercariService.updateNegotiationCustomer(favoriteId, customerId);
      return response.data?.success ?? false;
    } catch (error) {
      console.error('Error updating negotiation customer:', error);
      return false;
    }
  };

  const getMercariAccounts = async () => {
    try {
      const response = await MercariService.getMercariAccounts();
      const accounts = response.data?.accounts || [];
      setMercariAccounts(accounts);
      return accounts;
    } catch (err) {
      console.error('Error fetching mercari accounts:', err);
      return [];
    }
  };

  const getDashboardItems = async () => {
    setIsLoadingDashboard(true);
    setDashboardError(null);
    try {
      const response = await MercariService.getDashboardItems();
      if (response.data) {
        setDashboardItems(response.data.items || []);
        return response.data;
      } else {
        setDashboardError('Failed to fetch dashboard items');
        return [];
      }
    } catch (err) {
      console.error('Error in getDashboardItems:', err);
      setDashboardError(err.response?.data?.message || 'An error occurred while fetching dashboard items');
      return [];
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  return {
    pendingChanges,
    error,
    isLoading,
    isStatusLoading,
    negotiationLists,
    isLoadingNegotiations,
    negotiationError,
    negotiationPagination,
    takeCase,
    completeCase,
    addAuctionItem,
    updateAuctionItem,
    getAuctionItemsByFinishDate,
    setAuctionItemStatus,
    getPendingChanges,
    getNegotiationLists,
    addNegotiation,
    untrackNegotiation,
    updateNegotiationCustomer,
    dashboardItems,
    isLoadingDashboard,
    dashboardError,
    getDashboardItems,
    mercariAccounts,
    getMercariAccounts,
  };
}

export default useMercari;
